// API and data transformation utilities
import { formatDate, formatTime } from './dateUtils';

export const parseLocation = (locationStr) => {
  if (!locationStr) return [];
  
  const locations = [];
  // Split by semicolon to separate distinct location groups
  const groups = locationStr.split(';');
  
  groups.forEach(group => {
    const groupText = group.trim();
    
    // Extract any parenthetical information (e.g. "(Sec: 01, 02, 03)" or "(Females)")
    const parenMatch = groupText.match(/\(([^)]+)\)/);
    let parenthetical = '';
    if (parenMatch) {
      parenthetical = parenMatch[1].trim();
    }
    // Remove the parenthetical part for easier building-room extraction
    const mainPart = groupText.replace(/\([^)]+\)/, '').trim();
    
    // Handle formats like "24-120, 121" (multiple rooms with same building)
    // First check for the pattern with a building number followed by a room with optional letters
    const multiRoomMatch = mainPart.match(/^(\d+)-(\d+[A-Za-z]*)(?:,\s*(.+))?$/);
    if (multiRoomMatch) {
      const building = multiRoomMatch[1].trim();
      const firstRoom = multiRoomMatch[2].trim();
      
      // Add the first room
      if (parenthetical && parenthetical.toLowerCase().startsWith('sec:')) {
        const secText = parenthetical.replace(/^sec[:]?/i, '').trim();
        const sections = secText.split(/[,;]+/).map(s => s.trim()).filter(s => s);
        sections.forEach(sec => {
          locations.push({ building, room: firstRoom, section: sec });
        });
      } else {
        locations.push({ building, room: firstRoom, section: parenthetical || '' });
      }
      
      // If there are additional rooms after comma
      if (multiRoomMatch[3]) {
        const otherRooms = multiRoomMatch[3].split(',').map(r => r.trim()).filter(r => r);
        otherRooms.forEach(roomPart => {
          // Check if this is a full building-room or just a room number
          if (roomPart.includes('-')) {
            const parts = roomPart.split('-');
            if (parts.length >= 2) {
              const bldg = parts[0].trim();
              const rm = parts[1].trim();
              if (parenthetical && parenthetical.toLowerCase().startsWith('sec:')) {
                const secText = parenthetical.replace(/^sec[:]?/i, '').trim();
                const sections = secText.split(/[,;]+/).map(s => s.trim()).filter(s => s);
                sections.forEach(sec => {
                  locations.push({ building: bldg, room: rm, section: sec });
                });
              } else {
                locations.push({ building: bldg, room: rm, section: parenthetical || '' });
              }
            }
          } else {
            // Just a room number, use the same building
            if (parenthetical && parenthetical.toLowerCase().startsWith('sec:')) {
              const secText = parenthetical.replace(/^sec[:]?/i, '').trim();
              const sections = secText.split(/[,;]+/).map(s => s.trim()).filter(s => s);
              sections.forEach(sec => {
                locations.push({ building, room: roomPart, section: sec });
              });
            } else {
              locations.push({ building, room: roomPart, section: parenthetical || '' });
            }
          }
        });
      }
      
      return;
    }
    
    // Try matching a simple "Building-Room" pattern (e.g., "57-319", "24-236A")
    // Note: now includes support for alphanumeric room numbers like "236A"
    const brMatch = mainPart.match(/^(\d+)-(\d+[A-Za-z]*)$/);
    if (brMatch) {
      const building = brMatch[1].trim();
      const room = brMatch[2].trim();
      
      if (parenthetical && parenthetical.toLowerCase().startsWith('sec:')) {
        const secText = parenthetical.replace(/^sec[:]?/i, '').trim();
        const sections = secText.split(/[,;]+/).map(s => s.trim()).filter(s => s);
        sections.forEach(sec => {
          locations.push({ building, room, section: sec });
        });
      } else {
        locations.push({ building, room, section: parenthetical || '' });
      }
      
      return;
    }
    
    // Additional pattern for formats without hyphens but with spacing (e.g. "Building 24 Room 120")
    const noHyphenMatch = mainPart.match(/(?:Building|Bldg\.?)\s*(\d+)(?:\s*(?:Room|Rm\.?)\s*(\d+[A-Za-z]*))?/i);
    if (noHyphenMatch) {
      const building = noHyphenMatch[1].trim();
      const room = noHyphenMatch[2] ? noHyphenMatch[2].trim() : 'All';
      
      if (parenthetical && parenthetical.toLowerCase().startsWith('sec:')) {
        const secText = parenthetical.replace(/^sec[:]?/i, '').trim();
        const sections = secText.split(/[,;]+/).map(s => s.trim()).filter(s => s);
        sections.forEach(sec => {
          locations.push({ building, room, section: sec });
        });
      } else {
        locations.push({ building, room, section: parenthetical || '' });
      }
      
      return;
    }
    
    // Fallback: try to extract any groups of digits or alphanumerics
    const digits = mainPart.match(/\d+[A-Za-z]*/g);
    if (digits && digits.length >= 2) {
      const building = digits[0];
      const room = digits[1];
      
      if (parenthetical && parenthetical.toLowerCase().startsWith('sec:')) {
        const secText = parenthetical.replace(/^sec[:]?/i, '').trim();
        const sections = secText.split(/[,;]+/).map(s => s.trim()).filter(s => s);
        sections.forEach(sec => {
          locations.push({ building, room, section: sec });
        });
      } else {
        locations.push({ building, room, section: parenthetical || '' });
      }
    } else if (digits && digits.length === 1) {
      locations.push({
        building: digits[0],
        room: 'Unknown',
        section: parenthetical || ''
      });
    }
  });
  
  // Final fallback if no locations were found
  if (locations.length === 0 && locationStr.trim()) {
    console.warn(`Could not parse location: "${locationStr}"`);
    locations.push({
      building: 'Unknown',
      room: 'Unknown',
      section: locationStr.trim()
    });
  }
  
  return locations;
};

export const formatApiData = (apiData) => {
  if (!apiData || !apiData.data) return [];
  
  return apiData.data.flatMap(item => {
    const locations = parseLocation(item.location);
    
    // Fallback if no locations were parsed
    if (locations.length === 0) {
      return [{
        building: 'Unknown',
        room: 'Unknown',
        section: '',
        date: formatDate(item.date),
        time: formatTime(item.time),
        course: `${item.course_number}: ${item.course_title}`
      }];
    }
    
    // Create an entry for every building-room-section combination
    return locations.map(loc => ({
      building: loc.building,
      room: loc.room,
      section: loc.section,
      date: formatDate(item.date),
      time: formatTime(item.time),
      course: `${item.course_number}: ${item.course_title}`
    }));
  });
};

// Add this function to your apiUtils.js file

export const fetchLatestTermCode = async () => {
  try {
    // Attempt to fetch the exam schedule page
    const response = await fetch(
      'https://registrar.kfupm.edu.sa/exams-grades/final-exam-schedule/',
      { 
        headers: {
          'Accept': 'text/html',
          'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch term codes: ${response.status}`);
    }
    
    // Parse the HTML content
    const html = await response.text();
    
    // Use regex to extract all term code options
    const optionPattern = /<option value="(\d+)">Term (\d+)<\/option>/g;
    const termCodes = [];
    let match;
    
    while ((match = optionPattern.exec(html)) !== null) {
      termCodes.push({
        code: match[1], // The value attribute (e.g., "202420")
        term: match[2], // The displayed term number (e.g., "242")
        label: `Term ${match[2]}` // Full label format (e.g., "Term 242")
      });
    }
    
    if (termCodes.length === 0) {
      throw new Error('No term codes found on the page');
    }
    
    // Sort term codes in descending order (highest/most recent first)
    termCodes.sort((a, b) => parseInt(b.code) - parseInt(a.code));
    
    // Return the highest term code and its label
    return {
      termCode: termCodes[0].code,
      termLabel: termCodes[0].label,
      success: true
    };
  } catch (error) {
    console.error('Error fetching latest term code:', error);
    return {
      termCode: null, 
      termLabel: null,
      error: error.message,
      success: false
    };
  }
};